import type { Node } from '@xyflow/react';
import { graphViewModels } from '../adapter';
import type { HardwareGraph, HardwareNodeViewModel } from '../../types';

const elkPromise = import('elkjs/lib/elk.bundled.js').then(({ default: ELK }) => new ELK());

function dimensions(node: HardwareNodeViewModel) {
  if (node.kind === 'register') return { width: 190, height: 118 };
  if (node.kind === 'mux') return { width: 156, height: Math.max(116, 54 + Number(node.metadata.inputCount ?? 2) * 22) };
  if (node.kind === 'arithmetic') return { width: 154, height: 106 };
  if (node.kind === 'logic') return { width: 148, height: Math.max(92, 48 + Number(node.metadata.inputCount ?? 2) * 18) };
  if (node.kind === 'comparison') return { width: 148, height: 92 };
  if (node.kind === 'input' || node.kind === 'output') return { width: 132, height: 34 };
  if (node.kind === 'constant') return { width: 118, height: 34 };
  return { width: 160, height: 68 };
}

function accessibility(node: HardwareNodeViewModel) {
  return {
    focusable: true,
    ariaLabel: `${node.kind} ${node.label}, ${node.width}-bit hardware component. Use left and right arrow keys to follow connected logic.`,
  };
}

export function provisionalNodes(graph: HardwareGraph): Node<HardwareNodeViewModel>[] {
  return graphViewModels(graph).map((data, index) => ({
    id: data.id,
    type: `hw-${data.kind}`,
    data,
    position: { x: (index % 4) * 210, y: Math.floor(index / 4) * 145 },
    ...accessibility(data),
    ...dimensions(data),
  }));
}

export async function layoutWithElk(graph: HardwareGraph): Promise<Node<HardwareNodeViewModel>[]> {
  const elk = await elkPromise;
  const models = graphViewModels(graph);
  const result = await elk.layout({
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'RIGHT',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.spacing.nodeNode': '46',
      'elk.layered.spacing.nodeNodeBetweenLayers': '104',
      'elk.layered.nodePlacement.strategy': 'NETWORK_SIMPLEX',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
      'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
      'elk.padding': '[top=42,left=42,bottom=42,right=42]',
    },
    children: models.map((node) => ({ id: node.id, ...dimensions(node) })),
    edges: graph.edges.map((edge) => ({ id: edge.id, sources: [edge.source], targets: [edge.target] })),
  });
  const positions = new Map((result.children ?? []).map((node) => [node.id, node]));
  return models.map((data) => {
    const position = positions.get(data.id);
    return {
      id: data.id,
      type: `hw-${data.kind}`,
      data,
      position: { x: position?.x ?? 0, y: position?.y ?? 0 },
      ...accessibility(data),
      ...dimensions(data),
    };
  });
}
